import React, { useMemo, useRef } from 'react';
import { Line } from 'react-chartjs-2';
import { Timer, TimerReset, ServerCrash, ScanEye } from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { useTranslation } from 'react-i18next';
import { getCountryByCode, uses12HourFormat } from '../utils/countries';
import './ResponseTimeChart.css';

// Chart.js'i kaydet
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

function ResponseTimeChart({ checks, user, period = '24h', selectedDate }) {
  const chartRef = useRef(null);
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language || 'en';
  
  const userCountry = user?.country || 'US';
  const countryInfo = getCountryByCode(userCountry);
  const timezone = countryInfo?.timezone || 'UTC';
  const use12Hour = uses12HourFormat(userCountry);

  // Veriyi saatlik gruplara ayır
  const chartData = useMemo(() => {
    if (!checks || checks.length === 0) return { labels: [], datasets: [], hourlyData: [] };

    const hourlyData = {};

    // ✅ 24 saatlik slot'lar oluştur (0-23 arası)
    for (let hour = 0; hour < 24; hour++) {
      hourlyData[hour] = {
        hour: hour,
        responseTimes: [],
        successCount: 0,
        errorCount: 0,
        dnsErrors: 0,
        tcpErrors: 0,
        sslErrors: 0,
        httpErrors: 0
      };
    }

    // ✅ Check'leri saatlere dağıt
    checks.forEach(check => {
      const checkDate = new Date(check.checked_at);
      
      // ✅ Kullanıcının timezone'ında saat kaç?
      // toLocaleString ile timezone'a çevir, sadece saati al
      const timeStr = checkDate.toLocaleString('en-US', {
        timeZone: timezone,
        hour: 'numeric',
        hour12: false
      });
      const hour = parseInt(timeStr);

      if (hourlyData[hour]) {
        const data = hourlyData[hour];
        
        if (check.response_time) {
          data.responseTimes.push(check.response_time);
        }
        
        if (check.status === 'success') {
          data.successCount++;
        } else {
          data.errorCount++;
          if (check.dns_status === 'fail') data.dnsErrors++;
          if (check.tcp_status === 'fail') data.tcpErrors++;
          if (check.ssl_status === 'fail') data.sslErrors++;
          if (check.http_status === 'fail') data.httpErrors++;
        }
      }
    });

    // Veriyi formatla
    const sortedHours = Object.values(hourlyData).sort((a, b) => a.hour - b.hour);

    // Label'ları oluştur
    const labels = sortedHours.map(d => {
      // ✅ UTC saatini kullanıcının timezone'ındaki saate çevir
      // Backend zaten kullanıcının timezone'ına göre veri göndermiş
      // Bu yüzden UTC saatini direkt kullanıcının lokal saati olarak göster
      const hour = d.hour;
      
      if (use12Hour) {
        const period = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour % 12 || 12;
        return `${String(displayHour).padStart(2, '0')}:00 ${period}`;
      } else {
        return `${String(hour).padStart(2, '0')}:00`;
      }
    });

    const avgResponseTimes = sortedHours.map(d => 
      d.responseTimes.length > 0 
        ? Math.round(d.responseTimes.reduce((a, b) => a + b, 0) / d.responseTimes.length)
        : null
    );

    const uptimePercentages = sortedHours.map(d => {
      const total = d.successCount + d.errorCount;
      return total > 0 ? Math.round((d.successCount / total) * 100) : null;
    });

    const errorCounts = sortedHours.map(d => d.errorCount);

    return {
      labels,
      hourlyData: sortedHours,
      datasets: [
        {
          label: t('chart.responseTime') || 'Response Time (ms)',
          data: avgResponseTimes,
          borderColor: 'rgb(75, 192, 192)',
          backgroundColor: 'rgba(75, 192, 192, 0.1)',
          yAxisID: 'y',
          tension: 0.4,
          fill: true,
          pointRadius: 3,
          pointHoverRadius: 6,
          borderWidth: 2,
          spanGaps: true
        },
        {
          label: t('chart.uptime') || 'Uptime (%)',
          data: uptimePercentages,
          borderColor: 'rgb(54, 162, 235)',
          backgroundColor: 'rgba(54, 162, 235, 0.1)',
          yAxisID: 'y1',
          tension: 0.4,
          fill: true,
          pointRadius: 3,
          pointHoverRadius: 6,
          borderWidth: 2,
          spanGaps: true
        },
        {
          label: t('chart.errors') || 'Errors',
          data: errorCounts,
          borderColor: 'rgb(255, 99, 132)',
          backgroundColor: 'rgba(255, 99, 132, 0.1)',
          yAxisID: 'y2',
          tension: 0.4,
          fill: true,
          pointRadius: 3,
          pointHoverRadius: 6,
          borderWidth: 2,
          spanGaps: true
        }
      ]
    };
  }, [checks, use12Hour, t]);

  // Chart options
  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 13,
            weight: '500'
          }
        }
      },
      title: {
        display: true,
        text: t('chart.title') || 'Performance Metrics - Hourly Breakdown',
        font: {
          size: 16,
          weight: 'bold'
        },
        padding: 20
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#333',
        bodyColor: '#666',
        borderColor: '#ddd',
        borderWidth: 1,
        padding: 12,
        boxPadding: 6,
        usePointStyle: true,
        callbacks: {
          title: function(context) {
            return context[0].label;
          },
          label: function(context) {
            const label = context.dataset.label || '';
            const value = context.parsed.y;
            
            if (label.includes('Response Time') || label.includes(t('chart.responseTime'))) {
              return `${label}: ${value !== null ? value + 'ms' : t('chart.noData') || 'No data'}`;
            } else if (label.includes('Uptime') || label.includes(t('chart.uptime'))) {
              return `${label}: ${value !== null ? value + '%' : t('chart.noData') || 'No data'}`;
            } else {
              return `${label}: ${value !== null ? value : t('chart.noData') || 'No data'}`;
            }
          },
          afterBody: function(context) {
            const index = context[0].dataIndex;
            const hourData = chartData.hourlyData[index];
            
            if (hourData) {
              const lines = [];
              lines.push(`\n${t('chart.totalChecks') || 'Total Checks'}: ${hourData.successCount + hourData.errorCount}`);
              lines.push(`✓ ${t('chart.success') || 'Success'}: ${hourData.successCount}`);
              lines.push(`✗ ${t('chart.errors') || 'Errors'}: ${hourData.errorCount}`);
              
              if (hourData.errorCount > 0) {
                if (hourData.dnsErrors > 0) lines.push(`  DNS: ${hourData.dnsErrors}`);
                if (hourData.tcpErrors > 0) lines.push(`  TCP: ${hourData.tcpErrors}`);
                if (hourData.sslErrors > 0) lines.push(`  SSL: ${hourData.sslErrors}`);
                if (hourData.httpErrors > 0) lines.push(`  HTTP: ${hourData.httpErrors}`);
              }
              
              return lines;
            }
            return [];
          }
        }
      }
    },
    scales: {
      x: {
        display: true,
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.05)'
        },
        ticks: {
          maxRotation: 45,
          minRotation: 45,
          font: {
            size: 11
          }
        }
      },
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        title: {
          display: true,
          text: t('chart.responseTime') || 'Response Time (ms)',
          font: {
            size: 12,
            weight: 'bold'
          }
        },
        grid: {
          color: 'rgba(75, 192, 192, 0.1)'
        },
        ticks: {
          font: {
            size: 11
          }
        }
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        title: {
          display: true,
          text: t('chart.uptime') || 'Uptime (%)',
          font: {
            size: 12,
            weight: 'bold'
          }
        },
        min: 0,
        max: 100,
        grid: {
          drawOnChartArea: false,
        },
        ticks: {
          font: {
            size: 11
          }
        }
      },
      y2: {
        type: 'linear',
        display: false,
        position: 'right',
        min: 0,
        grid: {
          drawOnChartArea: false,
        }
      }
    }
  }), [t, chartData]);

  // Özet istatistikler
  const stats = useMemo(() => {
    if (!chartData.hourlyData || chartData.hourlyData.length === 0) {
      return { avgResponse: 0, avgUptime: 0, totalErrors: 0, totalChecks: 0 };
    }

    const validResponses = chartData.hourlyData
      .flatMap(d => d.responseTimes)
      .filter(r => r > 0);
    
    const avgResponse = validResponses.length > 0
      ? Math.round(validResponses.reduce((a, b) => a + b, 0) / validResponses.length)
      : 0;

    const totalSuccess = chartData.hourlyData.reduce((sum, d) => sum + d.successCount, 0);
    const totalErrors = chartData.hourlyData.reduce((sum, d) => sum + d.errorCount, 0);
    const totalChecks = totalSuccess + totalErrors;
    const avgUptime = totalChecks > 0 ? Math.round((totalSuccess / totalChecks) * 100) : 0;

    return { avgResponse, avgUptime, totalErrors, totalChecks };
  }, [chartData]);

  if (!checks || checks.length === 0) {
    return (
      <div className="chart-container">
        <div className="chart-empty">
          <div className="empty-icon">📊</div>
          <h3>{t('chart.noDataTitle') || 'No Data Available'}</h3>
          <p>{t('chart.noDataDescription') || 'There are no checks recorded for the selected period.'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="chart-container">
      {/* Özet Kartlar */}
      <div className="chart-summary">
        <div className="summary-card">
          <div className="summary-header">
            <span className="summary-icon"><Timer size={18} strokeWidth={2} color="#ffffff" /></span>
            <span className="summary-label">{t('chart.avgResponseTime') || 'Avg Response Time'}</span>
          </div>
          <div className="summary-value">{stats.avgResponse}ms</div>
          <div className="summary-subtitle">{t('chart.avgResponseSubtitle') || 'Average across all checks'}</div>
        </div>

        <div className="summary-card">
          <div className="summary-header">
            <span className="summary-icon"><TimerReset size={18} strokeWidth={2} color="#ffffff" /></span>
            <span className="summary-label">{t('chart.uptimeLabel') || 'Uptime'}</span>
          </div>
          <div className="summary-value" style={{ color: stats.avgUptime >= 95 ? '#10b981' : '#ef4444' }}>
            {stats.avgUptime}%
          </div>
          <div className="summary-subtitle">
            {stats.totalChecks} {t('chart.totalChecksLabel') || 'total checks'}
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-header">
            <span className="summary-icon"><ServerCrash size={18} strokeWidth={2} color="#ffffff" /></span>
            <span className="summary-label">{t('chart.totalErrorsLabel') || 'Total Errors'}</span>
          </div>
          <div className="summary-value" style={{ color: stats.totalErrors > 0 ? '#ef4444' : '#10b981' }}>
            {stats.totalErrors}
          </div>
          <div className="summary-subtitle">
            {stats.totalErrors === 0 
              ? (t('chart.allSystemsOperational') || 'All systems operational')
              : (t('chart.issuesDetected') || 'Issues detected')
            }
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-header">
            <span className="summary-icon"><ScanEye size={18} strokeWidth={2} color="#ffffff" /></span>
            <span className="summary-label">{t('chart.dataPointsLabel') || 'Data Points'}</span>
          </div>
          <div className="summary-value">{chartData.hourlyData.length}</div>
          <div className="summary-subtitle">{t('chart.hoursMonitored') || 'Hours monitored'}</div>
        </div>
      </div>

      {/* Ana Grafik */}
      <div className="chart-wrapper">
        <Line ref={chartRef} data={chartData} options={options} />
      </div>
    </div>
  );
}

export default ResponseTimeChart;
